import React, { useEffect } from "react";
import { useRouter } from "next/router";
import { useStoreState } from "pullstate";
import classNames from "classnames";

import styles from "src/pages/podcast/Podcast.module.scss";
import Container from "@/components/Container";
import PageWrapper from "@/components/PageWrapper";
import { apiClient } from "@/helpers/apiClient";
import { apiPath } from "@/helpers/apiPath";
import { GridColumns } from "@/components/Container/Container.interface";
import Typography from "@/components/Typography";
import MetaHead from "@/components/MetaHead";
import Recommended from "@/components/Recommended";
import SimilarPodcasts from "@/components/SimilarPodcasts";
import { routePath } from "@/helpers/routePath";
import PodcastEpisodes from "@/components/PodcastEpisodes";
import SkeletonLoader from "@/components/SkeletonLoader";
import PodcastTopAd from "@/components/Advertisement/PodcastTop";
import PodcastBottomAd from "@/components/Advertisement/PodcastBottom";
import { PodcastPageInterface } from "@/helpers/pageHelpers/Podcast/interface";
import useRequest from "@/helpers/useRequest";
import { Schema } from "@/components/MetaHead/Head.interface";
import { UIStore } from "@/store/UIStore";
import SidebarAd from "@/components/Advertisement/Sidebar";
import { getImgixURL } from "@/helpers/getImgixURL";
import Custom404 from "src/pages/404";
import Hosts from "@/components/Hosts";
import PodcastCover from "@/components/PodcastCover";
import { PodcastType } from "@/helpers/pageHelpers/Common/interface";
import TwitchIcon from "@/icons/twitch.svg";
import LargeTwitchIcon from "@/icons/large-twitch.svg";
import { VoteSection } from "@/components/VoteSection";
import { ContentSourceEnum } from "@/components/BookmarkButton/BookmarkButton.interface";
import {
  ContentType,
  VoteSectionVariant,
} from "@/components/VoteSection/VoteSection.interface";

export const PodcastPage: React.FC<PodcastPageInterface> = ({
  initialData,
}) => {
  const router = useRouter();

  const slug = router.query?.slug || initialData?.slug;
  const { data, mutate } = useRequest(slug && apiPath.PODCAST(slug), {
    initialData,
    revalidateOnMount: true,
  });

  const { userLoggedIn } = useStoreState(UIStore, (store) => store);

  useEffect(() => {
    mutate();
  }, [userLoggedIn, mutate]);

  if (router.isFallback) {
    return (
      <PageWrapper
        pageData={{ headerLabel: "Podcast" }}
        className={styles.pageWrapper}
      >
        <div className={styles.top}>
          <Container grid={GridColumns.Three}>
            <Typography
              variant="heading"
              component="h1"
              className={styles.title}
            >
              <SkeletonLoader height={40} />
            </Typography>

            <div className={styles.info}>
              <div className={styles.cover}>
                <SkeletonLoader />
              </div>

              <Typography variant="large">
                <SkeletonLoader height={100} />
              </Typography>
            </div>
          </Container>
        </div>

        <Container grid={GridColumns.Three}>
          <div className={styles.episodes}>
            <Typography variant="h3" component="h5" className={styles.heading}>
              <SkeletonLoader height={40} />
            </Typography>

            <SkeletonLoader height={400} />
          </div>
        </Container>
      </PageWrapper>
    );
  }

  if (!data || data?.error || !slug) {
    return <Custom404 />;
  }

  const {
    artwork,
    title,
    nextScheduled,
    headMetaData,
    picture,
    logoPicture,
    tags,
    followed,
    hosts,
    description,
    podcastType,
  } = data;

  const pageData = {
    headerLabel: title,
    fallbackHistoryBack: {
      backUrl: routePath.PODCASTS.href,
      as: routePath.PODCASTS.href,
    },
  };
  const schemaData = {
    type: Schema.Podcast,
    title: title,
  };
  const image = getImgixURL(logoPicture);

  return (
    <>
      <PageWrapper pageData={pageData} className={styles.pageWrapper}>
        <MetaHead metaData={headMetaData} schemaData={schemaData} />

        <PodcastCover
          cover={picture}
          title={title}
          description={description}
          schedule={nextScheduled}
          tags={tags}
          artwork={artwork}
          logo={image}
          slug={slug}
          followed={followed}
          podcastType={podcastType}
          hosts={hosts}
        />

        <Container>
          <PodcastTopAd isPodcast />
        </Container>

        {!!initialData.episodes?.items.length && (
          <Container grid={GridColumns.Three} className={styles.container}>
            {podcastType === PodcastType.Twitch && (
              <div className={styles.twitch}>
                <div className={styles.content}>
                  <div className={styles.titleContainer}>
                    <TwitchIcon className="hide-on-mobile" />
                    <LargeTwitchIcon
                      className={classNames("hide-on-desktop", styles.icon)}
                    />
                    <Typography variant="medium" className={styles.title}>
                      Od teraz podcast do obejrzenia na żywo na Twitch.tv
                    </Typography>
                  </div>
                  <Typography variant="medium">
                    Zobacz ten i inne programy newonce streamowane na żywo.
                  </Typography>
                </div>
                <a
                  href="https://www.twitch.tv/newonce_"
                  className={styles.button}
                >
                  Przejdź na Twitch.tv
                </a>
              </div>
            )}
            <div className={styles.episodes}>
              <Typography
                variant="h3"
                component="h5"
                className={styles.heading}
              >
                Epizody podcastu
              </Typography>

              {!!initialData.episodes?.items && (
                <PodcastEpisodes slug={slug} episodes={initialData.episodes} />
              )}
            </div>

            <div className={styles.sidebar}>
              <Hosts hosts={hosts} className={styles.hosts} />
              <div className={styles.display}>
                <VoteSection
                  source={ContentSourceEnum.Podcast}
                  type={ContentType.PODCAST}
                  variant={VoteSectionVariant.Sidebar}
                  data={data}
                  className={styles.voteSection}
                />
                <SidebarAd />
              </div>
            </div>
          </Container>
        )}

        <Container>
          <PodcastBottomAd />
        </Container>

        <Container>
          <SimilarPodcasts slug={slug} isMobile />
        </Container>
        <VoteSection
          source={ContentSourceEnum.Podcast}
          type={ContentType.PODCAST}
          data={data}
          className={classNames(styles.voteSection, styles.voteDesktop)}
        />
        <Recommended />
      </PageWrapper>
    </>
  );
};

export async function getStaticPaths() {
  const client = apiClient();

  const {
    data: { items },
  } = await client.get(apiPath.PODCASTS({ page: 1 }));

  const paths =
    items &&
    items.map((podcast) => ({
      params: { slug: podcast.slug },
    }));

  return {
    paths,
    fallback: true,
  };
}

export async function getStaticProps(context) {
  const { params } = context;
  const client = apiClient();

  try {
    const { data } = await client.get(apiPath.PODCAST(params.slug));
    const { data: episodes } = await client.get(
      apiPath.EPISODES({ slug: params.slug, page: 1 })
    );

    return {
      props: {
        initialData: {
          ...data,
          episodes: episodes,
        },
      },
      revalidate: 86400,
    };
  } catch (e) {
    return {
      props: {
        initialData: {
          error: true,
        },
      },
      revalidate: 43200,
    };
  }
}

export default PodcastPage;
